import {
    Banner,
    BlockStack,
    Button,
    Card,
    Checkbox,
    Divider,
    InlineStack,
    Layout,
    Page,
    RadioButton,
    SkeletonBodyText,
    Text,
    TextField,
} from '@shopify/polaris';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import agent from '../../app/api/agent';
import { getApiErrorMessage } from '../../app/api/error';
import { LegacyAdminSettings } from '../../app/models/LegacyMode';

const LegacySettingsPage = () => {
    const [settings, setSettings] = useState<LegacyAdminSettings>();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState<{ tone: 'success' | 'critical'; text: string }>();

    useEffect(() => {
        agent.LegacyAdmin.settings()
            .then(setSettings)
            .catch(async error => setMessage({ tone: 'critical', text: await getApiErrorMessage(error, 'Unable to load legacy settings.') }))
            .finally(() => setLoading(false));
    }, []);

    const set = <K extends keyof LegacyAdminSettings>(key: K, value: LegacyAdminSettings[K]) =>
        setSettings(current => current ? { ...current, [key]: value } : current);

    const save = async () => {
        if (!settings) return;
        setSaving(true);
        setMessage(undefined);
        try {
            const saved = await agent.LegacyAdmin.updateSettings(settings);
            // Refresh the app-data bootstrap after the database save so the
            // extension's next first paint uses the same PFV1 labels.
            await agent.Settings.legacyStatus();
            setSettings(saved);
            setMessage({ tone: 'success', text: 'Legacy storefront settings were saved.' });
        } catch (error) {
            setMessage({ tone: 'critical', text: await getApiErrorMessage(error, 'Unable to save legacy settings.') });
        } finally {
            setSaving(false);
        }
    };

    if (loading || !settings) {
        return <Page title="Settings"><Card><SkeletonBodyText lines={8} /></Card></Page>;
    }

    return (
        <Page title="Settings" subtitle="These options preserve the behavior current merchants already use.">
            <Layout>
                {message && <Layout.Section><Banner tone={message.tone}><p>{message.text}</p></Banner></Layout.Section>}
                <Layout.Section>
                    <Card>
                        <BlockStack gap="400">
                            <Text as="h2" variant="headingMd">Labels</Text>
                            <Text as="p" tone="subdued">Change the text shown by the storefront selector.</Text>
                            <InlineStack gap="300" wrap>
                                <div style={{ minWidth: 260, flex: 1 }}><TextField label="Make" value={settings.promptMake} onChange={value => set('promptMake', value)} autoComplete="off" /></div>
                                <div style={{ minWidth: 260, flex: 1 }}><TextField label="Model" value={settings.promptModel} onChange={value => set('promptModel', value)} autoComplete="off" /></div>
                                <div style={{ minWidth: 260, flex: 1 }}><TextField label="Submodel" value={settings.promptSubmodel} onChange={value => set('promptSubmodel', value)} autoComplete="off" /></div>
                                <div style={{ minWidth: 260, flex: 1 }}><TextField label="Year" value={settings.promptYear} onChange={value => set('promptYear', value)} autoComplete="off" /></div>
                                <div style={{ minWidth: 260, flex: 1 }}><TextField label="Search button" value={settings.promptSearch} onChange={value => set('promptSearch', value)} autoComplete="off" /></div>
                                <div style={{ minWidth: 260, flex: 1 }}><TextField label="Clear button" value={settings.promptClear} onChange={value => set('promptClear', value)} autoComplete="off" /></div>
                            </InlineStack>
                        </BlockStack>
                    </Card>
                </Layout.Section>

                <Layout.Section>
                    <Card>
                        <BlockStack gap="400">
                            <Text as="h2" variant="headingMd">General settings</Text>
                            <Checkbox
                                label="Enable submodel filter"
                                checked={settings.submodelEnabled}
                                onChange={value => set('submodelEnabled', value)}
                                helpText={
                                    <span>
                                        The vehicle editor and storefront will include Make, Model, Submodel, and Year. Also enable Submodel in the Auto Part Search block&apos;s theme settings for correct storefront rendering.{' '}
                                        <Link to="/legacy/documentation#vehicle-search-bar-2">View theme setup instructions</Link>.
                                    </span>
                                }
                            />
                            <Checkbox label="Arrange search inputs in Year, Make, Model order" checked={settings.ymmEnabled} onChange={value => set('ymmEnabled', value)} />
                            <Checkbox label="Enable persistent filtering" checked={settings.globalFilter} onChange={value => set('globalFilter', value)} helpText="Collection links automatically retain the selected vehicle." />
                            <Checkbox label="Automatically search after the shopper completes vehicle selection" checked={settings.automaticSearch} onChange={value => set('automaticSearch', value)} />
                            <Checkbox label="Sort years in ascending order" checked={settings.sortYearsAscending} onChange={value => set('sortYearsAscending', value)} />
                        </BlockStack>
                    </Card>
                </Layout.Section>

                <Layout.Section>
                    <Card>
                        <BlockStack gap="400">
                            <Text as="h2" variant="headingMd">Shopify filtering method</Text>
                            <RadioButton
                                label="Legacy collection tag URL"
                                helpText="Uses collection paths such as /collections/all/vehicle-tag for compatibility with older and customized themes."
                                checked={settings.collectionTagFilterEnabled}
                                id="legacy-filtering"
                                name="filtering-method"
                                onChange={() => set('collectionTagFilterEnabled', true)}
                            />
                            <RadioButton
                                label="Shopify storefront filtering (Search & Discovery)"
                                helpText="Uses Shopify's native product tag query-string filter."
                                checked={!settings.collectionTagFilterEnabled}
                                id="native-filtering"
                                name="filtering-method"
                                onChange={() => set('collectionTagFilterEnabled', false)}
                            />
                        </BlockStack>
                    </Card>
                </Layout.Section>

                <Layout.Section>
                    <Card>
                        <BlockStack gap="400">
                            <Text as="h2" variant="headingMd">Advanced settings</Text>
                            <Checkbox label="Always redirect to full product results after search" checked={settings.redirectToResults} onChange={value => set('redirectToResults', value)} />
                            <Checkbox label="Redirect to a custom search path" checked={settings.customRedirectEnabled} onChange={value => set('customRedirectEnabled', value)} />
                            <TextField label="Custom search path" value={settings.redirectPath} onChange={value => set('redirectPath', value)} disabled={!settings.customRedirectEnabled} autoComplete="off" helpText="Use {vehicle} for the selected tag and {collection} for the current collection handle." />
                            <Divider />
                            <Checkbox label="Stay on the current page after Clear" checked={settings.stayOnCurrentPageAfterClear} onChange={value => set('stayOnCurrentPageAfterClear', value)} />
                            <Checkbox label="Include universal products" checked={settings.includeUniversalCollections} onChange={value => set('includeUniversalCollections', value)} />
                            <Checkbox label="Supplement mappings with a Make-Model tag" checked={settings.useTwoTags} onChange={value => set('useTwoTags', value)} />
                            <Checkbox label="Supplement mappings with Make and Make-Model tags" checked={settings.useThreeTags} onChange={value => set('useThreeTags', value)} />
                            <Checkbox label="Enable tag prefix (ymm_)" checked={settings.tagPrefixEnabled} onChange={value => set('tagPrefixEnabled', value)} />
                            <Checkbox label="Allow partial search" checked={settings.partialSearch} onChange={value => set('partialSearch', value)} />
                            <Checkbox label="Allow make selection through metafield" checked={settings.allowMakeSelectionThroughMetafield} onChange={value => set('allowMakeSelectionThroughMetafield', value)} />
                            <Checkbox label="Enable collection redirect" checked={settings.collectionRedirectEnabled} onChange={value => set('collectionRedirectEnabled', value)} />
                            <Checkbox label="Save garage vehicle tags to customer profiles" checked={settings.saveTagToCustomerProfile} onChange={value => set('saveTagToCustomerProfile', value)} />
                        </BlockStack>
                    </Card>
                </Layout.Section>

                <Layout.Section>
                    <InlineStack align="end"><Button variant="primary" loading={saving} onClick={save}>Save settings</Button></InlineStack>
                </Layout.Section>
            </Layout>
        </Page>
    );
};

export default LegacySettingsPage;
